import React, { useState } from "react";
import { ReactComponent as EditIcon } from "../../assests/edit.svg";
import { ReactComponent as DisableIcon } from "../../assests/disable.svg";
import { formatString } from "../../utils/dataformatter/Rawformatter";
import { useAxiosContext } from "../../context/axiosinstance";
import ComboBox from "@/components/ui/combobox";
import { saveData } from "@screens/preliminarystaffing/viewproject";
import {
  actionNameConfig,
  SocketIdType,
} from "@configure/menuAndSubmenuConfig";
import { useLocation } from "react-router-dom";
import { Check } from "lucide-react";

export const convertMonthsToYears = (months) => {
  const years = months / 12;
  return years % 1 === 0 ? years.toFixed(0) : years.toFixed(1);
};

const possibleModules = {
  "/scoped-dashboard": "scoped",
  "/deal-review-dashboard": "dealreview",
};

export const EditForm = ({
  id,
  index,
  item,
  state,
  dispatch,
  isLoading,
  resources,
  projectid,
  deal_id,
  isAuthenticated,
  getData = () =>{}
}) => {
  const { isButtonDisabled } = useAxiosContext();
  const [showMoreIndexes, setShowMoreIndexes] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const pathname = useLocation().pathname;

  // get the module from the pathname
const moduleName =
  Object.entries(possibleModules).find(([key]) =>
    pathname.startsWith(key)
  )?.[1] || null;


  const handleEdit = () => {
    setEditMode(true);
  };

  const handleSave = async () => {
    const saveDetails = {
      additional_info: {
        hs_deal_id: deal_id,
        delivery_manager: state["Delivery Manager"].employeeid || "NA",
        deal_review_preparer:
          state["Deal Review Preparer"].employeeid.join(";") || "NA",
      },
      projectid: projectid,
      module: moduleName,
    };
    setEditMode(false);
    await saveData(saveDetails, projectid, {
      "Socket-Action":
      moduleName === "dealreview"
        ? actionNameConfig.DEALREVIEW.ADDITIONAL_INFO_UPDATION_IN_DEALREVIEW
        : actionNameConfig.SCOPED.ADDITIONAL_INFO_UPDATION_IN_SCOPED,
      "Socket-Deal-Id": projectid,
      "Socket-Id-Type": SocketIdType.ProjectId,
      "Socket-Deal-Status": null,
    });
    getData(false)
  };

  const toggleShowMore = () => {
    setShowMoreIndexes((prev) => !prev);
  };

  const handleChange = (newValue) => {
    if (Array.isArray(newValue)) {
      const label = newValue;
      const employeeid = resources
        .filter((resource) => newValue.includes(resource.fullname))
        .map((item) => item.employeeid);
      dispatch({
        type: "SET_DEAL_REVIEW_PREPARER",
        payload: { label, employeeid: employeeid },
      });
      return;
    }
    const label = newValue;
    const employeeid =
      resources.find((resource) => resource.fullname === newValue)
        ?.employeeid || "NA";
    dispatch({ type: "SET_DELIVERY_MANAGER", payload: { label, employeeid } });
  };

  const value = item?.multiple ? state[item?.subLabel]?.label?.filter(v => Boolean(v?.trim())) || [] : state[item?.subLabel]?.label?.trim() || "";

  return (
    <div
      key={index}
      data-testid={`${id}-container-${index}`}
      className="flex items-center justify-between text-[#1C1C1C] text-sm font-normal leading-5 text-left p-3 rounded-lg bg-background/30"
    >
      {/* Label + SubLabel */}
      <div className="flex flex-col basis-[60%] gap-2">
        {editMode ? (
          <ComboBox
            id={`addInfo-select-${index}`}
            name="fullname"
            multiselect={item.multiple}
            options={resources.map((resource) => resource.fullname)}
            value={value}
            onChange={(value) => handleChange(value)}
            disabled={item.disabled}
            placeholder={
              Array.isArray(state[item.subLabel].label)
                ? state[item.subLabel].label.join(", ")
                : state[item.subLabel].label
            }
          />
        ) : Array.isArray(state[item.subLabel].label) ? (
          <div className="flex flex-row gap-1">
            <p className="text-md font-semibold break-words whitespace-normal text-foreground">
              {state[item.subLabel].label.length === 0 ||
              state[item.subLabel].label.every((label) => label.trim() === "")
                ? "NA"
                : showMoreIndexes
                ? state[item.subLabel].label.join(", ")
                : state[item.subLabel].label.slice(0, 3).join(", ")}
            </p>
            {state[item.subLabel].label.length > 3 && (
              <span
                className="text-md text-foreground cursor-pointer whitespace-nowrap"
                onClick={() => toggleShowMore()}
              >
                {showMoreIndexes
                  ? "Show less"
                  : `+ ${state[item.subLabel].label.length - 3} more`}
              </span>
            )}
          </div>
        ) : (
          <p
            className="text-md font-semibold truncate line-clamp-2 text-foreground"
            title={state[item.subLabel].label}
          >
            {!state[item.subLabel].label ||
            state[item.subLabel].label.trim() === ""
              ? "NA"
              : state[item.subLabel].label}
          </p>
        )}

        <span className="text-xs text-muted-foreground">{item.subLabel}</span>
      </div>

      {/* Icon Container */}
      <div className="flex items-center w-6 h-6">
        {item.type === "edit" && isAuthenticated ? (
          editMode ? (
            <Check
              id={`addInfo-done-${index}`}
              className={`text-[#A1E3CB] cursor-pointer ${
                isLoading?.[index] ? "opacity-50 pointer-events-none" : ""
              }`}
              onClick={async () => await handleSave()}
            />
          ) : (
            <span
              id={`btnaddInfoedit${formatString(item.subLabel)}`}
              role="button"
              aria-disabled={isButtonDisabled}
              tabIndex={index}
              className="w-[18.75px] h-[18.75px] cursor-pointer [aria-disabled='true']:(cursor-not-allowed)"
              onClick={() => !isButtonDisabled && handleEdit()}
            >
              <EditIcon className="text-foreground" />
            </span>
          )
        ) : (
          <span
            id={`btnaddInfodisable${index}`}
            className="w-[18.75px] h-[18.75px] cursor-not-allowed"
          >
            <DisableIcon className="text-iconmuted/40" />
          </span>
        )}
      </div>
    </div>
  );
};
